import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { daysAgo } from "../../services/hiveActions";
import { POOR_HEALTH, daysUntil, rankHives } from "./siteRollup";
import { Link } from "react-router-dom";

/**
 * The first layer of the dashboard (#282): what needs a person right now.
 *
 * Capped, and that is the entire point. This list is the same size with
 * three hives and with three hundred, so the top of the dashboard never
 * becomes something to scroll past. Everything below it is structure; this
 * is the answer.
 *
 * Deliberately ignores sites. An urgent issue at the furthest yard belongs
 * above a routine check at the nearest one -- the keeper can decide the
 * driving; the app should not quietly make that decision for them by
 * sorting geographically.
 */

const SHOW_AT_MOST = 6;

function reasonFor(hive, issueLevel, health) {
  // Says why this hive is here, in the words a beekeeper would use. The
  // rank that put it here is invisible, so the row has to explain itself.
  //
  // THE COLOUR FOLLOWS THE RANK (reported 28 Aug). It used to be chosen
  // per reason, independently, and had drifted out of agreement with
  // rankHives: an "Open issue" wore yellow while a "Never checked" below
  // it wore red, so the list looked mis-sorted -- a milder-looking row
  // sitting above a more alarming one.
  //
  // rankHives is explicit about why that order is right: an issue and a
  // failing health score are EVIDENCE something is wrong, where an
  // overdue or missing check is only the absence of evidence. Red now
  // means evidence, amber means absence, and the two cannot disagree
  // again without somebody editing this comment.
  if (issueLevel === "urgent") return { label: "Urgent issue", tone: "danger" };
  if (issueLevel) return { label: "Open issue", tone: "danger" };
  if (typeof health === "number" && health < POOR_HEALTH) {
    return { label: `Health ${health}`, tone: "danger" };
  }
  // Amber, not red: nobody has looked, which is not the same as knowing
  // something is wrong -- and it ranks below an open issue for exactly
  // that reason.
  if (!hive.last_checked) return { label: "Never checked", tone: "warning" };

  const days = daysUntil(hive.next_check_due);
  if (days !== null && days < 0) {
    const late = Math.abs(days);
    return { label: `${late} day${late === 1 ? "" : "s"} overdue`, tone: "warning" };
  }
  return { label: "Due", tone: "warning" };
}

export function NeedsAttentionPanel({ hives, issuesByHive = {}, healthByHive = {}, onOpen, onMarkChecked, invites = [] }) {
  const ranked = rankHives(hives, issuesByHive, healthByHive).filter(
    (h) =>
      Boolean(issuesByHive[h.hive_id]) ||
      h.needs_attention ||
      !h.last_checked ||
      (typeof healthByHive[h.hive_id] === "number" && healthByHive[h.hive_id] < POOR_HEALTH),
  );

  // Nothing wrong is worth SAYING, not worth hiding. An empty dashboard
  // section reads as broken; "everything is up to date" reads as done.
  //
  // But an unanswered invitation is something needing you, so this must
  // not claim otherwise -- saying "everything is up to date" above a
  // pending invite is worse than saying nothing (#312).
  if (ranked.length === 0 && invites.length === 0) {
    return (
      <Card title="Needs you now" className="section-spacing">
        <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
          Nothing is overdue and no issues are open. Everything is up to date.
        </p>
      </Card>
    );
  }

  const shown = ranked.slice(0, SHOW_AT_MOST);
  const remaining = ranked.length - shown.length;

  return (
    <Card title="Needs you now" className="section-spacing">
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
        {/* Above every hive, which is what mobile's RANK.INVITE = 0 has
            always said (#312). Web kept invites in a separate card whose
            own comment argued they should come first -- "the only one
            about you rather than about your bees, and someone is waiting
            on the answer" -- without acting on it. A separate card is
            easier to scroll past on a busy dashboard.

            Links through to Team rather than accepting inline: accepting
            grants access to somebody else's account, and that is worth
            one deliberate step on a screen that says what is being
            joined. */}
        {invites.map((inv) => (
          <li
            key={`invite-${inv.share_id}`}
            style={{
              display: "flex", alignItems: "center", gap: "var(--space-2)",
              padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)",
              flexWrap: "wrap",
            }}
          >
            <Badge tone="warning">Invitation</Badge>
            <Link to="/team" className="touch-target" style={{ color: "var(--color-text)" }}>
              <strong>{inv.primary_email || "Someone"}</strong> invited you to help with their hives
            </Link>
            <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
              {inv.role ? `as ${inv.role}` : "review to answer"}
            </span>
          </li>
        ))}

        {shown.map((hive) => {
          const reason = reasonFor(hive, issuesByHive[hive.hive_id], healthByHive[hive.hive_id]);
          return (
            <li
              key={hive.hive_id}
              style={{
                display: "flex", alignItems: "center", gap: "var(--space-2)",
                padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)",
                flexWrap: "wrap",
              }}
            >
              <Badge tone={reason.tone}>{reason.label}</Badge>
              <button
                type="button"
                onClick={() => onOpen?.(hive.hive_id)}
                style={{
                  background: "none", border: "none", padding: 0, font: "inherit",
                  color: "var(--color-text)", cursor: "pointer", textAlign: "left",
                  textDecoration: "underline",
                }}
              >
                <strong>Hive {hive.hive_number}</strong>
                {hive.site_name ? ` at ${hive.site_name}` : ""}
              </button>
              {/*
                * WHOSE APIARY, WHEN IT IS NOT YOURS (#504).
                *
                * Reported 6 Oct: the dashboard separates your hives from
                * the ones you help with everywhere EXCEPT here -- "that
                * could be an issue if I choose a name that's the same as
                * one used by someone I'm helping."
                *
                * Not hypothetical. Hive numbering is per account, so two
                * people both running "Hive 1" is the normal case, and
                * this is the list somebody acts on in a hurry.
                *
                * NAMED ON THE ROW rather than grouping the section,
                * because the sort order here is urgency and should stay
                * that way -- grouping would push somebody else's urgent
                * hive below your routine one. The section's job is
                * triage; the row just has to say where it is.
                *
                * Nothing is added for your own hives: a badge on every
                * row saying "yours" is noise on the common case.
                */}
              {hive.is_my_account === false && hive.account_label && (
                <Badge tone="neutral">{hive.account_label}</Badge>
              )}
              <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                {hive.last_checked ? `checked ${daysAgo(hive.last_checked)}` : "no inspections yet"}
              </span>
              {onMarkChecked && (
                <Button
                  size="sm"
                  variant="secondary"
                  style={{ marginLeft: "auto" }}
                  onClick={() => onMarkChecked(hive.hive_id)}
                >
                  Mark Checked
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {/* The count matters more than the rows would. Someone with 40
          things outstanding needs to know that; showing all 40 here would
          just recreate the list this panel exists to replace. */}
      {remaining > 0 && (
        <p style={{ margin: "var(--space-2) 0 0", color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          and {remaining} more {remaining === 1 ? "hive" : "hives"} needing attention — see the sites below.
        </p>
      )}
    </Card>
  );
}
